import {
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableState,
} from "@tanstack/react-table";
import { useEffect, useMemo } from "react";
import {
  ArrayParam,
  NumberParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import useDataTable, {
  type DataTableOptions,
  type ReactTableOptions,
} from "common/components/DataTable/useDataTable";
import usePageSize from "common/hooks/usePageSize";

interface UsePaginatedDataTableOptions<TData extends { id: string }, TValue> {
  reactTableOptions: ReactTableOptions<TData, TValue>;
  dataTableOptions: DataTableOptions<TData>;
  isLoading: boolean;
}

export default function usePaginatedDataTable<
  TData extends { id: string },
  TValue,
>({
  reactTableOptions,
  dataTableOptions,
  isLoading,
}: UsePaginatedDataTableOptions<TData, TValue>) {
  const { pageSize, onPageSizeChange } = usePageSize(
    dataTableOptions.localStorageId,
  );

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 1), {
    removeDefaultsFromUrl: true,
    updateType: "replaceIn",
  });

  const [sort, setSort] = useQueryParam("sort", withDefault(ArrayParam, []), {
    removeDefaultsFromUrl: true,
    updateType: "replaceIn",
  });

  const sorting: SortingState = useMemo(
    () =>
      sort
        .map((sortItem: string) => {
          const [id, order] = sortItem.split(":");
          return { id, desc: order === "desc" };
        })
        .filter(({ id }) => id),
    [sort],
  );

  // Convert 1-based URL page to 0-based pageIndex for react-table.
  const pageIndex = Math.max(0, page - 1);

  const state: Partial<TableState> = {
    sorting,
    pagination: { pageIndex: isLoading ? 0 : pageIndex, pageSize },
  };

  const tableOptions: ReactTableOptions<TData, TValue> = {
    ...reactTableOptions,
    state,
    manualPagination: false,
    data:
      isLoading || !reactTableOptions.data
        ? new Array(pageSize).fill({})
        : reactTableOptions.data,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: (updaterOrValue) => {
      const newPagination =
        typeof updaterOrValue === "function"
          ? updaterOrValue({ pageIndex, pageSize })
          : updaterOrValue;

      // Convert 0-based pageIndex back to 1-based page number in the URL.
      setPage(newPagination.pageIndex + 1);
      onPageSizeChange(newPagination.pageSize);
    },
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sorting)
          : updaterOrValue;

      const sortArray = newSorting.map(
        ({ id, desc }) => `${id}:${desc ? "desc" : "asc"}`,
      );

      setSort(sortArray);
    },
    autoResetPageIndex: false,
  };

  const table = useDataTable<TData, TValue>(tableOptions, dataTableOptions);

  const { getRowModel, getRowCount, firstPage } = table;
  const dataLength = reactTableOptions.data?.length ?? 0;

  useEffect(() => {
    if (getRowModel().rows.length === 0 && getRowCount() !== 0 && !isLoading) {
      firstPage();
    }
  }, [firstPage, getRowCount, getRowModel, isLoading, dataLength]);

  return { table };
}
